/**
 * BottomSheet (PH9-1) — canonical modal that becomes a bottom drawer
 * on mobile and a centred modal on desktop.
 *
 * Pattern: backdrop blur + click-outside-closes + Esc-closes. Mobile
 * comes up from the bottom edge (`items-end`); desktop (≥md) centres
 * with `md:items-center`. Body scroll is locked while open.
 *
 * Future PH4-8 QuickLog refactor + Settings sub-sheets reuse this.
 * EditPhasesModal-style inline implementations should migrate when
 * touched.
 */
import { useEffect } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional sheet header — title text + optional subtitle. */
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** Override the max width on desktop. Default `max-w-lg`. */
  maxWidth?: string;
  /** Forwarded to the inner panel for tests. */
  testId?: string;
  /** When true, click on backdrop does NOT close (form mid-edit). */
  disableBackdropClose?: boolean;
  /** Show the mobile drag-handle indicator. Default true (mobile UX cue). */
  showHandle?: boolean;
}

export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "max-w-lg",
  testId = "bottom-sheet",
  disableBackdropClose = false,
  showHandle = true,
}: BottomSheetProps) {
  const { t } = useTranslation("common");
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center animate-backdrop-in"
      onClick={() => {
        if (!disableBackdropClose) onClose();
      }}
      data-testid={`${testId}-backdrop`}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "var(--shadow-drawer)" }}
        className={`w-full ${maxWidth} bg-bg-surface md:rounded-card rounded-t-card max-h-[92dvh] overflow-y-auto animate-sheet-in`}
      >
        {showHandle && (
          <div
            className="md:hidden w-10 h-1 rounded-full bg-bg-divider mx-auto mt-2 mb-1"
            aria-hidden="true"
            data-testid={`${testId}-handle`}
          />
        )}
        {(title || subtitle) && (
          <header className="sticky top-0 bg-bg-surface/95 backdrop-blur px-5 py-3 border-b border-bg-divider flex items-start justify-between gap-3">
            <div className="min-w-0">
              {title && (
                <h3 className="text-base font-semibold text-fg-primary leading-tight">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-xs text-fg-secondary mt-0.5">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("a11y.close", { defaultValue: "Закрыть" })}
              data-testid={`${testId}-close`}
              className="shrink-0 w-7 h-7 rounded-full bg-bg-raised text-fg-muted hover:text-fg-primary flex items-center justify-center"
            >
              <X size={14} />
            </button>
          </header>
        )}
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
